import {compose} from '../../lib/compose';
import { ApolloClient, InMemoryCache, ApolloLink, Observable, gql } from "@apollo/client";
import { HttpLink } from "@apollo/client/link/http";
import { ServerError } from '@apollo/client/errors';
import { ApolloProvider } from "@apollo/client/react";
import { createErrorLink } from './errorLink';
import { RetryLink } from "@apollo/client/link/retry";
import { SetContextLink } from '@apollo/client/link/context';
import { useRef, useEffect, useState } from 'react';
import { createRemoteJWKSet, jwtVerify } from 'jose';
import { getEnv } from "../../getEnv";
import { endKeycloakSession } from './signOut';
import withAlerts from '../../lib/withAlerts';
import {logger} from '../../lib/logger';
import {
  getAuthStore,
  setAuthVerified,
  clearAuthVerified,
  isAuthenticated
} from './sessionStore';

const terminationGuardLink = new ApolloLink((operation, forward) => {
  if (sessionStorage.getItem('unauthenticated') === 'true') {
    return new Observable(observer => {
      const timer = setTimeout(() => {
        observer.error(new Error('Session terminated — operation cancelled'));
      }, 0);
      return () => clearTimeout(timer);
    });
  }
  return forward(operation);
});

const AuthedApolloProvider = (props) => {
  const { children, alerts: { error } } = props;
  const clientRef = useRef(null);
  const env = getEnv();

  // Single status enum — atomic transitions, no gap between client ready and children rendering
  const [status, setStatus] = useState(() =>
    sessionStorage.getItem('unauthenticated') === 'true' ? 'terminated' : 'pending'
  );

  // Listen for termination from errorLink or endKeycloakSession
  useEffect(() => {
    const handleTermination = () => setStatus('terminated');
    window.addEventListener('session-terminated', handleTermination);
    return () => window.removeEventListener('session-terminated', handleTermination);
  }, []);

  useEffect(() => {
    if (status === 'terminated') return;

    // Guards against a second setup() run completing after this effect
    // instance has been superseded (e.g. StrictMode double-invoke, or any
    // future remount before the first async chain resolves) — without this,
    // an overlapping setup() could still assign clientRef.current and flip
    // status after this instance has already moved on.
    let cancelled = false;

    const setup = async () => {
      // Verify JWT on new session — skip on browser refresh (auth_verified already set)
      if (!isAuthenticated()) {
        try {
          const { access_token } = getAuthStore();
          if (access_token) {
            // Use JWKS endpoint directly — jose handles key fetching, 
            // formatting, and algorithm matching automatically
            const JWKS = createRemoteJWKSet(
              new URL(`${env.keycloak_host}/realms/${env.keycloak_realm}/protocol/openid-connect/certs`)
            );
            
            await jwtVerify(access_token, JWKS, {
              issuer: `${env.keycloak_issuer}/realms/${env.keycloak_realm}`,
            });

            setAuthVerified();
          }
        } catch (err) {
          logger.error('JWT verification failed');
          clearAuthVerified();
          await endKeycloakSession('invalid token');
          return;
        }
      }

      if (cancelled) return;

      const retry = new RetryLink({
        delay: { initial: 200, max: 3000, jitter: true },
        attempts: { 
          max: 2,
          retryIf: (error) => {
            if (!error) return false;
            if (ServerError.is(error)) {
              try {
                if (JSON.parse(error.bodyText)?.errors?.length) return false;
              } catch { /* not JSON */ }
            }
            return true;
          }
        }
      });

      const httpLink = new HttpLink({ uri: '/api' });

      const setAuth = new SetContextLink((prevContext, operation) => {
        const { access_token } = getAuthStore();
        return {
          headers: {
            ...prevContext.headers,
            authorization: `Bearer ${access_token || null}`
          }
        };
      });

      const errorLink = createErrorLink(error, env);

      if (cancelled) return;

      clientRef.current = new ApolloClient({
        cache: new InMemoryCache(),
        link: ApolloLink.from([terminationGuardLink, errorLink, ApolloLink.concat(setAuth, ApolloLink.concat(retry, httpLink))]),

        defaultOptions: {
          watchQuery: { fetchPolicy: 'cache-and-network', errorPolicy: 'all' },
          query:       { fetchPolicy: 'cache-first', errorPolicy: 'all' },
          mutate:      { errorPolicy: 'all' },
        }
      });

      // Session probe — first GQL call after Apollo is built
      // If session is superseded, errorLink catches it here and sets terminated
      // so children (UserAgreement etc.) never mount
      try {
        await clientRef.current.query({
          query: gql`query { ping }`,
          fetchPolicy: 'no-cache'
        });
      } catch (err) {
        logger.error('Session probe failed');
      }

      if (cancelled) return;

      // Only transition to ready if probe didn't trigger termination
      if (sessionStorage.getItem('unauthenticated') !== 'true') {
        setStatus('ready');
      }
    };

    setup();

    return () => { cancelled = true; };
  }, []);

  if (status === 'terminated') { return null; }
  if (status === 'pending')    { return null; }
  if (!clientRef.current)      { return null; }

  return (
    <ApolloProvider client={clientRef.current}>
      {children}
    </ApolloProvider>
  );
};

export default compose(withAlerts)(AuthedApolloProvider);
